import {
  Building2,
  Calendar,
  CalendarDays,
  Clock,
  FolderOpen,
  House,
  KeyRound,
  type LucideIcon,
  Settings,
  User,
  Users,
  UsersRound,
} from 'lucide-react';

import { paths } from '@/app/paths';
import type { Permissions } from '@escala/domain/permissions';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Rotas filhas que também acendem o item. */
  match?: string[];
  /** Descrição curta no submenu de um grupo ("Funções e regras"). */
  description?: string;
}

/** Item que agrupa páginas (Cadastros): abre um submenu ao lado, ou a lista no celular. */
export interface NavGroup {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

export type NavEntry = NavItem | NavGroup;

export const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

/**
 * Cadastros: Organizações (só a plataforma), Ministérios, Membros, Eventos e Acessos (só o
 * Administrador). Os líderes veem Ministérios, Membros e Eventos.
 */
function registryItems(permissions: Permissions): NavItem[] {
  return [
    ...(permissions.manageOrgs
      ? [{ to: paths.orgs, label: 'Organizações', icon: Building2, description: 'Igrejas da plataforma' }]
      : []),
    { to: paths.ministries, label: 'Ministérios', icon: Users, description: 'Funções e regras' },
    {
      to: paths.members,
      label: 'Membros',
      icon: User,
      match: [paths.me],
      description: 'Pessoas e disponibilidade',
    },
    { to: paths.events, label: 'Eventos', icon: CalendarDays, description: 'Eventos e recorrências' },
    ...(permissions.manageAccess
      ? [{ to: paths.accesses, label: 'Acessos', icon: KeyRound, description: 'Convites e perfis' }]
      : []),
  ];
}

/**
 * Itens da sidebar conforme o perfil (o membro vê só a própria agenda e o perfil). A
 * equipe também é escalada: tem "Minha agenda" logo abaixo do Dashboard — menos quem
 * não é membro da organização aberta (`agenda: false`, a plataforma em outra igreja).
 */
export function navItems(permissions: Permissions, options: { agenda?: boolean } = {}): NavEntry[] {
  if (!permissions.isManager) {
    return [
      { to: paths.home, label: 'Minha agenda', icon: House },
      { to: paths.schedules, label: 'Calendário', icon: Calendar },
      { to: paths.me, label: 'Meu perfil', icon: User },
    ];
  }

  return [
    { to: paths.home, label: 'Dashboard', icon: House },
    ...(options.agenda !== false ? [{ to: paths.agenda, label: 'Minha agenda', icon: Clock }] : []),
    { to: paths.schedules, label: 'Calendário', icon: Calendar, match: [paths.generate] },
    ...(permissions.viewMembership ? [{ to: paths.membership, label: 'Membresia', icon: UsersRound }] : []),
    { label: 'Cadastros', icon: FolderOpen, items: registryItems(permissions) },
    ...(permissions.manageSettings ? [{ to: paths.settings, label: 'Configurações', icon: Settings }] : []),
  ];
}

export function isActive(item: NavEntry, pathname: string): boolean {
  if (isGroup(item)) return item.items.some((child) => isActive(child, pathname));
  if (item.to === '/') return pathname === '/';

  return [item.to, ...(item.match ?? [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
